import { useEffect, useState } from "react";

import InterruptionSettings from "../VoiceSettings/InterruptionSettingsV2";
import WelcomeMessageSettings from "../VoiceSettings/WelcomeMessageSettingsV3/WelcomeMessageSettings";
import { TimeZoneSettings } from "./TimeZoneSettingsV2/TimeZoneSettings";
import { ParentLayout } from "../AssistantTools/components/ParentLayout";
import { SectionLayout } from "../AssistantTools/components/SectionLayout";
import { MainLayout } from "../AssistantTools/components/MainLayout";
import { SectionItem } from "../AssistantTools/components/SectionItem";
import BoostKeywords from "../VoiceSettings/BoostKeywordsV2";
import TtsVocabulary from "../VoiceSettings/TtsVocabulary";
import VoiceCache from "../VoiceCacheV2/VoiceCache";
import ConfidenceSettings from "../VoiceSettings/ConfidenceSettings";
import VoicemailAndCallOnHoldSettings from "../VoiceSettings/CallSettings/VoicemailAndCallOnHoldSettings";
import CallRecordingAndDisclaimerSettings from "../VoiceSettings/CallSettings/CallRecordingsAndDisclaimerSettings";
import CallDurationSettings from "../VoiceSettings/CallSettings/CallDurationSettings";
import InactivitySettings from "../VoiceSettings/InactivitySettingsV2";
import BackgroundAmbience from "../VoiceSettings/BackgroundAmbience";
import PastContextSettings from "../PastContextSettings";
import Noise from "../VoiceSettings/Noise";
import LanguageSettings from "../LanguageSettings";

enum SETTINGS {
  WELCOME_MESSAGE = "Welcome Message",
  KEYWORDS = "Assistant Vocabulary",
  LANGUAGE = "Language Detection",
  OFFICE_HOURS = "Office Hours",
  INACTIVITY = "Inactivity",
  INTERRUPTION = "Interruption",
  CALL_SETTINGS = "Call Duration",
  VOICEMAIL = "Voicemail & On-hold",
  RECORDING = "Call Recording & Disclaimer",
  NOISE_CANCELLATION = "Noise Cancellation",
  BACKGROUND_AMBIENCE = "Background Ambience",
  SPEECH_UNCERTAINITY = "Speech Uncertainity",
  PAST_CONTEXT = "Conversation History",
  VOICE_CACHE = "Voice Cache",
  OFFICE_HOURS = "Office Hours",
  LANGUAGE = "Language Detection",
}

const SettingsComponent = {
  [SETTINGS.INACTIVITY]: (props) => <InactivitySettings {...props} />,
  [SETTINGS.INTERRUPTION]: (props) => <InterruptionSettings {...props} />,
  [SETTINGS.WELCOME_MESSAGE]: (props) => <WelcomeMessageSettings {...props} />,
  [SETTINGS.KEYWORDS]: (props) => (
    <>
      <BoostKeywords {...props} />
      <TtsVocabulary {...props} />
    </>
  ),
  [SETTINGS.OFFICE_HOURS]: (props) => <TimeZoneSettings {...props} />,
  [SETTINGS.RECORDING]: (props) => (
    <CallRecordingAndDisclaimerSettings {...props} />
  ),
  [SETTINGS.VOICEMAIL]: (props) => (
    <VoicemailAndCallOnHoldSettings {...props} />
  ),
  [SETTINGS.VOICE_CACHE]: (props) => <VoiceCache {...props} />,
  [SETTINGS.CALL_SETTINGS]: (props) => <CallDurationSettings {...props} />,
  [SETTINGS.SPEECH_UNCERTAINITY]: (props) => <ConfidenceSettings {...props} />,
  [SETTINGS.BACKGROUND_AMBIENCE]: (props) => <BackgroundAmbience {...props} />,
  [SETTINGS.NOISE_CANCELLATION]: (props) => <Noise {...props} />,
  [SETTINGS.PAST_CONTEXT]: (props) => <PastContextSettings {...props} />,
  [SETTINGS.LANGUAGE]: (props) => <LanguageSettings {...props} />,
};

function VoiceAssistantSettings(props) {
  const {
    voiceSettings,
    timezoneSettings,
    transcriptionSettings,
    syncAssistant,
    workspaceId,
    assistantId,
    locked,
    assistants,
    pastContextSettings,
    languageSettings
  } = props;

  const [activeSetting, setActiveSetting] = useState(SETTINGS.WELCOME_MESSAGE);

  // Assistant Vocabulary (Boost Keywords + TTS Vocabulary) is shown for every
  // transcription provider. Speech Uncertainty relies on Deepgram confidence
  // thresholds only.
  const hideSpeechUncertainty =
    transcriptionSettings?.transcriptionProvider !== "deepgram";

  const isSettingHidden = (setting) =>
    setting === SETTINGS.SPEECH_UNCERTAINITY && hideSpeechUncertainty;

  const settings = Object.values(SETTINGS).reduce((acc, setting) => {
    if (isSettingHidden(setting)) {
      return acc;
    }

    return [
      ...acc,
      { name: setting, href: "#", current: setting === activeSetting },
    ];
  }, []);

  useEffect(() => {
    if (isSettingHidden(activeSetting)) {
      setActiveSetting(SETTINGS.WELCOME_MESSAGE);
    }
  }, [hideSpeechUncertainty]);

  return (
    <ParentLayout>
      <SectionLayout>
        {settings.map((setting) => (
          <SectionItem
            sectionName={setting.name}
            isActive={setting.current}
            onSectionClick={() => setActiveSetting(setting.name)}
          />
        ))}
      </SectionLayout>
      <MainLayout>
        {SettingsComponent[activeSetting]({
          ...voiceSettings,
          transcriptionSettings,
          timezoneSettings,
          syncAssistant,
          workspaceId,
          assistantId,
          showSlotTags: true,
          locked,
          assistants,
          pastContextSettings,
          languageSettings
        })}
      </MainLayout>
    </ParentLayout>
  );
}

export { VoiceAssistantSettings };
